import { useEffect, useState } from "react";

/** ---------------------- Tipos (LAB) ---------------------- */
export type SolicitanteItemAPI = {
  id: string | number;
  nome: string;
};

export type EquipReparoAPI = {
  case_ref: string | null;
  public_token?: string | null;

  equipamento_nome: string | null;

  solicitante_nome: string | null;
  solicitante_principal_id?: string | number | null;
  solicitante_principal_nome?: string | null;

  solicitantes_ids?: Array<string | number> | null;
  solicitantes_nomes?: string[] | null;
  solicitantes?: SolicitanteItemAPI[] | null;

  copiados_ids?: Array<string | number> | null;
  copiados_nomes?: string[] | null;
  copiados?: SolicitanteItemAPI[] | null;

  empresa_razao_social: string | null;

  prioridade: string | number | null;

  status_atual_id?: string | number | null;
  status_atual_nomenclatura?: string | null;
};

export type SolicitanteItem = {
  id: string;
  nome: string;
};

export type EquipReparo = {
  caseRef: string;
  publicToken: string;

  equipamentoNome: string;

  solicitanteNome: string;
  solicitantePrincipalId: string;
  solicitantePrincipalNome: string;

  solicitantesIds: string[];
  solicitantesNomes: string[];
  solicitantes: SolicitanteItem[];

  copiadosIds: string[];
  copiadosNomes: string[];
  copiados: SolicitanteItem[];

  empresaRazaoSocial: string;
  prioridade: string;

  statusAtualId: string;
  statusAtualNomenclatura: string;
};

type ListaResp = {
  ok?: boolean;
  fonte?: "cache" | "banco";
  versao?: number | null;
  limit?: number;
  data: EquipReparoAPI[];
};

/** ---------------------- Constantes (LAB) ---------------------- */
const URL =
  "https://api.eva-googlemarine.com/departamentos/laboratorio/acompanhamento/get-lista-equipamentos-reparo-info";

const ACCESS_TOKEN_KEY = "access_token";

/** ---------------------- Utils (gerais) ---------------------- */
function na(v: any): string {
  if (v === null || v === undefined) return "N/A";
  const s = String(v).trim();
  return s === "" ? "N/A" : s;
}

function toStringArray(arr: any): string[] {
  if (!Array.isArray(arr)) return [];
  return arr.map((x) => na(x)).filter((x) => x !== "N/A");
}

function toSolicitanteArray(arr: any): SolicitanteItem[] {
  if (!Array.isArray(arr)) return [];
  return arr
    .map((x) => ({
      id: na(x?.id),
      nome: na(x?.nome),
    }))
    .filter((x) => x.id !== "N/A" || x.nome !== "N/A");
}

function getAccessToken(): string | null {
  try {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  } catch {
    return null;
  }
}

/** ---------------------- Normalizers (LAB) ---------------------- */
function normalizeRow(r: EquipReparoAPI): EquipReparo {
  return {
    caseRef: na(r.case_ref),
    publicToken: na(r.public_token),

    equipamentoNome: na(r.equipamento_nome),

    solicitanteNome: na(r.solicitante_nome),
    solicitantePrincipalId: na(r.solicitante_principal_id),
    solicitantePrincipalNome: na(r.solicitante_principal_nome),

    solicitantesIds: toStringArray(r.solicitantes_ids),
    solicitantesNomes: toStringArray(r.solicitantes_nomes),
    solicitantes: toSolicitanteArray(r.solicitantes),

    copiadosIds: toStringArray(r.copiados_ids),
    copiadosNomes: toStringArray(r.copiados_nomes),
    copiados: toSolicitanteArray(r.copiados),

    empresaRazaoSocial: na(r.empresa_razao_social),
    prioridade: na(r.prioridade),

    statusAtualId: na(r.status_atual_id),
    statusAtualNomenclatura: na(r.status_atual_nomenclatura),
  };
}

/** ---------------------- HTTP (LAB) ---------------------- */
async function getLista(): Promise<{
  fonte: "cache" | "banco" | undefined;
  versao: number | null;
  data: EquipReparo[];
}> {
  const token = getAccessToken();
  const headers: Record<string, string> = { Accept: "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(URL, {
    method: "GET",
    headers,
    cache: "no-store",
  });

  if (!res.ok) throw new Error(`Erro HTTP ${res.status}`);

  const fonte = (res.headers.get("x-source") as "cache" | "banco") || undefined;

  const json = (await res.json()) as ListaResp;
  const versao = typeof json?.versao === "number" ? json.versao : null;

  const data: EquipReparo[] = (json?.data ?? []).map(normalizeRow);
  return { fonte, versao, data };
}

/** ---------------------- Hook (LAB) ---------------------- */
export function useListaEquipamentosReparo() {
  const [itens, setItens] = useState<EquipReparo[]>([]);
  const [loading, setLoading] = useState(true);
  const [fonte, setFonte] = useState<"cache" | "banco" | undefined>(undefined);
  const [versao, setVersao] = useState<number | null>(null);

  const carregar = async () => {
    setLoading(true);
    try {
      const lista = await getLista();
      setItens(lista.data);
      setFonte(lista.fonte);
      setVersao(lista.versao ?? null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { itens, loading, fonte, versao, refresh: carregar };
}
